import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaQuestionCircle, FaSearch, FaBook, FaEnvelope, FaPhone, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import StripeNavbar from './StripeNavbar';

export default function PublicHelpCenter() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const cookieLang = document.cookie.match(/taealam_language=([^;]+)/);
    return cookieLang ? cookieLang[1] : 'en';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const isArabic = lang === 'ar';
  const isMobile = window.innerWidth <= 768;

  const categories = [
    { id: 'all', label: isArabic ? 'الكل' : 'All', icon: <FaBook /> },
    { id: 'getting-started', label: isArabic ? 'البدء' : 'Getting Started', icon: <FaQuestionCircle /> },
    { id: 'account', label: isArabic ? 'الحساب' : 'Account', icon: <FaQuestionCircle /> },
    { id: 'booking', label: isArabic ? 'الحجوزات' : 'Bookings', icon: <FaQuestionCircle /> },
    { id: 'payment', label: isArabic ? 'المدفوعات' : 'Payments', icon: <FaQuestionCircle /> }
  ];

  const articles = {
    en: [
      {
        id: 1,
        category: 'getting-started',
        title: 'How to Create an Account',
        content: 'To create an account, click on the "Sign Up" button in the top right corner. You can sign up using your email address or social media accounts. After signing up, you\'ll need to verify your email address.'
      },
      {
        id: 2,
        category: 'getting-started',
        title: 'Finding the Right Teacher',
        content: 'Use our search filters to find teachers by subject, grade level, price range, or rating. You can also browse featured teachers on the homepage. Click on a teacher\'s profile to see their qualifications, availability, and reviews.'
      },
      {
        id: 3,
        category: 'booking',
        title: 'How to Book a Session',
        content: 'Once you find a teacher you like, click "View Profile" and then "Book Session". Select your preferred date and time from their available slots. Confirm your booking details and complete the payment to secure your session.'
      },
      {
        id: 4,
        category: 'account',
        title: 'Managing Your Profile',
        content: 'You can update your profile information, including your name, profile picture, and preferences, from your dashboard. Go to the Profile section to make changes.'
      },
      {
        id: 5,
        category: 'payment',
        title: 'Payment Methods',
        content: 'We accept major credit and debit cards. All payments are processed securely. You can add or update your payment methods from your account settings.'
      }
    ],
    ar: [
      {
        id: 1,
        category: 'getting-started',
        title: 'كيفية إنشاء حساب',
        content: 'لإنشاء حساب، انقر على زر "التسجيل" في الزاوية اليمنى العلوية. يمكنك التسجيل باستخدام عنوان بريدك الإلكتروني أو حسابات وسائل التواصل الاجتماعي. بعد التسجيل، ستحتاج إلى التحقق من عنوان بريدك الإلكتروني.'
      },
      {
        id: 2,
        category: 'getting-started',
        title: 'العثور على المعلم المناسب',
        content: 'استخدم فلاتر البحث للعثور على المعلمين حسب المادة أو المستوى الدراسي أو النطاق السعري أو التقييم. يمكنك أيضًا تصفح المعلمين المميزين على الصفحة الرئيسية. انقر على ملف المعلم الشخصي لرؤية مؤهلاته وتوافره والمراجعات.'
      },
      {
        id: 3,
        category: 'booking',
        title: 'كيفية حجز جلسة',
        content: 'بمجرد العثور على معلم يعجبك، انقر على "عرض الملف الشخصي" ثم "حجز جلسة". اختر التاريخ والوقت المفضلين من الأوقات المتاحة. أكد تفاصيل حجزك وأكمل الدفع لتأمين جلستك.'
      },
      {
        id: 4,
        category: 'account',
        title: 'إدارة ملفك الشخصي',
        content: 'يمكنك تحديث معلومات ملفك الشخصي، بما في ذلك اسمك وصورة الملف الشخصي والتفضيلات، من لوحة التحكم الخاصة بك. انتقل إلى قسم الملف الشخصي لإجراء التغييرات.'
      },
      {
        id: 5,
        category: 'payment',
        title: 'طرق الدفع',
        content: 'نقبل بطاقات الائتمان والخصم الرئيسية. تتم معالجة جميع المدفوعات بأمان. يمكنك إضافة أو تحديث طرق الدفع الخاصة بك من إعدادات حسابك.'
      }
    ]
  };

  const filteredArticles = articles[lang].filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'} style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <StripeNavbar lang={lang} setLang={setLang} alwaysScrolled={true} />
      
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '100px 20px 60px' : '120px 40px 80px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
            marginBottom: 24,
            color: 'white'
          }}>
            <FaQuestionCircle size={32} />
          </div>
          <h1 style={{
            fontSize: isMobile ? 32 : 42,
            fontWeight: 800,
            color: '#0F172A',
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'مركز المساعدة' : 'Help Center'}
          </h1>
          <p style={{
            fontSize: 18,
            color: '#64748B',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'ابحث عن إجابات لأسئلتك أو اتصل بنا'
              : 'Find answers to your questions or contact us'}
          </p>
        </div>

        {/* Search Bar */}
        <div style={{
          marginBottom: 40,
          maxWidth: 600,
          margin: '0 auto 40px'
        }}>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FaSearch style={{
              position: 'absolute',
              left: isArabic ? 'auto' : 20,
              right: isArabic ? 20 : 'auto',
              color: '#94A3B8',
              fontSize: 18
            }} />
            <input
              type="text"
              placeholder={isArabic ? 'ابحث عن موضوع...' : 'Search for a topic...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px 16px 50px',
                borderRadius: 12,
                border: '1px solid #E2E8F0',
                fontSize: 16,
                background: 'white',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                ...(isArabic ? { padding: '16px 50px 16px 20px' } : {})
              }}
            />
          </div>
        </div>

        {/* Categories */}
        <div style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: 40
        }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '10px 20px',
                borderRadius: 50,
                border: '1px solid #E2E8F0',
                background: selectedCategory === cat.id ? '#FF7426' : 'white',
                color: selectedCategory === cat.id ? 'white' : '#64748B',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.3s ease',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>

        {/* Articles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <div
                key={article.id}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  border: '1px solid #E2E8F0',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <button
                  onClick={() => toggleFaq(article.id)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16
                  }}
                >
                  <h3 style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: '#0F172A',
                    margin: 0,
                    flex: 1,
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                    ...(isArabic ? { textAlign: 'right' } : {})
                  }}>
                    {article.title}
                  </h3>
                  <div style={{
                    color: '#FF7426',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {expandedFaqs[article.id] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                  </div>
                </button>
                
                {expandedFaqs[article.id] && (
                  <div style={{
                    padding: '0 24px 20px 24px',
                    borderTop: '1px solid #E2E8F0'
                  }}>
                    <p style={{
                      fontSize: 16,
                      color: '#64748B',
                      lineHeight: 1.7,
                      margin: '16px 0 0 0',
                      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                    }}>
                      {article.content}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#64748B',
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}>
              {isArabic ? 'لا توجد نتائج' : 'No results found'}
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div style={{
          marginTop: 60,
          padding: '40px',
          background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
          borderRadius: 20,
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'لا تزال تحتاج إلى مساعدة؟' : 'Still need help?'}
          </h2>
          <p style={{
            fontSize: 16,
            marginBottom: 24,
            opacity: 0.95,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'تواصل معنا وسنكون سعداء لمساعدتك'
              : 'Contact us and we\'ll be happy to help'}
          </p>
          <div style={{
            display: 'flex',
            gap: 16,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a
              href="https://mail.google.com/mail/?view=cm&to=support@taealam.bh"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'white',
                color: '#FF7426',
                padding: '12px 24px',
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.3s ease',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}
            >
              <FaEnvelope /> {isArabic ? 'البريد الإلكتروني' : 'Email'}
            </a>
            <a
              href="tel:+97339030980"
              style={{
                background: 'white',
                color: '#FF7426',
                padding: '12px 24px',
                borderRadius: 50,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.3s ease',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}
            >
              <FaPhone /> {isArabic ? 'الهاتف' : 'Phone'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

